import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import List from '@pelckmans/elementis/components/list';
import SearchField from '@pelckmans/elementis/components/search-field';
import { useContext, useState } from 'react';
import ModalContext from '../../../../../../../contexts/ModalContext';
import { useTranslation } from 'react-i18next';
import { Member } from '../../../../../../../api/groups';

const BASE = 'PAGES.RESULTS.PER_STUDENT.MODALS.FILTER_STUDENTS';

const renderStudent = (member: Member) => `${member.lastName} ${member.firstName}`;

type Props = {
  id: string;
  allMembers: Member[];
  selectedMembers: Member[];
  setSelectedMembers: (members: Member[]) => void;
};

function FilterStudentsModal({ id, allMembers, selectedMembers, setSelectedMembers }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [membersToSelect, setMembersToSelect] = useState<Member[]>(
    selectedMembers.length === allMembers.length ? [] : selectedMembers,
  );

  const { closeModal } = useContext(ModalContext);

  const handleClose = () => {
    closeModal(id);
  };

  const filteredBySearchValue = search
    ? allMembers.filter(student => renderStudent(student).toLowerCase().includes(searchValue.toLowerCase()))
    : allMembers;

  return (
    <Dialog id={id} modal size="sm" onClose={handleClose} aria-label={t(`${BASE}.A11Y_LABEL`)}>
      <Dialog.Header
        title={t(`${BASE}.HEADER.TITLE`)}
        onClose={handleClose}
        aria-label={t(`${BASE}.HEADER.A11Y_LABEL`)}
      >
        <SearchField
          aria-label={t(`${BASE}.HEADER.SEARCH.A11Y_LABEL`)}
          clearActionLabel={t(`${BASE}.HEADER.SEARCH.CLEAR.A11Y_LABEL`)}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => {
            setSearchValue('');
            setSearch(false);
          }}
          onSearch={() => setSearch(true)}
          placeholder={t(`${BASE}.HEADER.SEARCH.PLACEHOLDER`)}
          searchActionLabel={t(`${BASE}.HEADER.SEARCH.SEARCH_BUTTON.LABEL`)}
          value={searchValue}
        />
      </Dialog.Header>
      <Dialog.Body>
        <List>
          {filteredBySearchValue.map(student => (
            <List.Item
              as="checkbox"
              key={student.id}
              checked={membersToSelect.some(s => s.id === student.id)}
              onClick={() => {
                setMembersToSelect(prev => {
                  if (prev.some(m => m.id === student.id)) {
                    return prev.filter(s => s.id !== student.id);
                  }
                  return [...prev, student];
                });
              }}
              title={renderStudent(student)}
            />
          ))}
        </List>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button
                key="select-all"
                prominence="ghost"
                onClick={() => {
                  setMembersToSelect(filteredBySearchValue);
                }}
              >
                {t('ACTIONS.SELECT_ALL')}
              </Button>
              <Button
                key="deselect-all"
                prominence="ghost"
                disabled={selectedMembers.length === 0}
                onClick={() => setMembersToSelect([])}
              >
                {t('ACTIONS.CLEAR_SELECTION')}
              </Button>
            </>
          ),
        }}
        buttonGroupPropsRight={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="close">
                {t('ACTIONS.CLOSE')}
              </Button>
              <Button
                key="apply"
                prominence="success"
                onClick={() => {
                  setSelectedMembers(membersToSelect);
                  handleClose();
                }}
              >
                {t('ACTIONS.APPLY')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default FilterStudentsModal;
